import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedHousehold } from '../../hooks/useSelectedHousehold';
import { useHouseholdMembers } from '../../hooks/useHousehold';
import { useActiveMember } from '../../hooks/useActiveMember';
import { useNotificationPreferences, useTogglePush } from '../../hooks/usePush';
import { useMyProfile } from '../../hooks/useUserProfile';
import { pushSupported, syncPushOnLaunch } from '../../lib/push';
import { Modal } from '../Modal';
import { BreakSheet } from '../BreakSheet';
import { MemberAvatar } from '../MemberAvatar';
import { Switch } from '../Switch';
import { tapLight, warn } from '../../lib/haptics';

const tabs = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/calendar', label: 'Calendar', icon: 'calendar-clear', end: false },
  { to: '/household', label: 'Household', icon: 'people', end: false },
];

const manageItems = [
  { to: '/assignments', label: 'Tasks', icon: 'checkbox', desc: 'View & manage tasks' },
  { to: '/rooms', label: 'Rooms', icon: 'grid', desc: 'Organise by space' },
  { to: '/chore-definitions', label: 'Chore library', icon: 'sparkles', desc: 'Define recurring chores' },
  { to: '/household-settings', label: 'Settings', icon: 'settings', desc: 'Points, skipping & more' },
];

const quickAdd = [
  { label: 'New task', sub: 'A one-off chore for today', icon: 'add-circle', to: '/assignments' },
  { label: 'New chore', sub: 'A recurring chore definition', icon: 'sparkles', to: '/chore-definitions' },
  { label: 'New room', sub: 'A space to group chores', icon: 'grid', to: '/rooms' },
];

export function AppLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { householdId, households } = useSelectedHousehold();
  const { data: members } = useHouseholdMembers(householdId);
  const { activeMember, switchableSharedMembers, isActingAsShared, switchTo } = useActiveMember(
    user?.sub ?? '',
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [breakOpen, setBreakOpen] = useState(false);

  const { data: prefs } = useNotificationPreferences();
  const togglePush = useTogglePush();
  const pushOn = prefs?.pushEnabled ?? false;

  const { data: profile } = useMyProfile();
  const currentBreak = profile?.break;
  const breakUntilLabel = currentBreak
    ? (() => {
        const [y, m, d] = currentBreak.returnDate.split('-').map(Number);
        return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(y!, m! - 1, d!));
      })()
    : '';

  // On launch, refresh the FCM token for users who've already consented and
  // re-sync the briefing timezone if the device has changed zones.
  useEffect(() => {
    if (pushSupported() && prefs?.pushEnabled) void syncPushOnLaunch(true, prefs.timezone);
  }, [prefs?.pushEnabled, prefs?.timezone]);

  const email = user?.email ?? '';
  const avatarLetter = email[0]?.toUpperCase() ?? '?';
  const currentHousehold = households?.find((h) => h.householdId === householdId);
  const primaryMember = members?.find((m) => m.userId === user?.sub);
  const headerMember = activeMember ?? primaryMember;
  const showSwitcher = switchableSharedMembers.length > 0;

  const handleSignOut = async () => {
    warn();
    setMenuOpen(false);
    await signOut();
    navigate('/signin');
  };

  const go = (to: string, state?: unknown) => {
    setAddOpen(false);
    setMenuOpen(false);
    navigate(to, state ? { state } : undefined);
  };

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <Link to="/" className="app-topbar-title" style={{ textDecoration: 'none' }}>
          Chor<i className="title-italics">tl</i><span className="title-tail">e</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {showSwitcher && (
            <button
              className="topbar-user-btn"
              aria-label={isActingAsShared ? `Acting as ${activeMember?.memberName}` : 'Switch user'}
              onClick={() => { tapLight(); setSwitchOpen(true); }}
            >
              {headerMember ? (
                <MemberAvatar member={headerMember} size="1.85rem" fontSize="0.8rem" />
              ) : (
                <div className="avatar" style={{ width: '1.85rem', height: '1.85rem', fontSize: '0.8rem' }}>{avatarLetter}</div>
              )}
              <ion-icon name="swap-horizontal" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }} />
            </button>
          )}
          <button
            className="topbar-icon-btn"
            aria-label="Home View"
            onClick={() => { tapLight(); navigate('/home-view'); }}
          >
            <ion-icon name="tv-outline" />
          </button>
        </div>
      </header>

      {isActingAsShared && (
        <button
          className="acting-as-banner"
          onClick={() => { tapLight(); switchTo(null); }}
        >
          <ion-icon name="eye-outline" />
          <span style={{ flex: 1, textAlign: 'left' }}>
            Acting as <strong>{activeMember?.memberName}</strong>
          </span>
          <span className="acting-as-back">Back to me</span>
        </button>
      )}

      <main className="app-main">
        <Outlet />
      </main>

      <nav className="bottom-nav" aria-label="Primary">
        <div className="bottom-nav-inner">
          <NavLink to={tabs[0]!.to} end className={({ isActive }) => `tab-item${isActive ? ' tab-item-active' : ''}`} onClick={() => tapLight()}>
            <ion-icon name={tabs[0]!.icon} />
            {tabs[0]!.label}
          </NavLink>
          <NavLink to={tabs[1]!.to} className={({ isActive }) => `tab-item${isActive ? ' tab-item-active' : ''}`} onClick={() => tapLight()}>
            <ion-icon name={tabs[1]!.icon} />
            {tabs[1]!.label}
          </NavLink>

          <button className="tab-fab" aria-label="Add" onClick={() => { tapLight(); setAddOpen(true); }}>
            <ion-icon name="add" />
          </button>

          <NavLink to={tabs[2]!.to} className={({ isActive }) => `tab-item${isActive ? ' tab-item-active' : ''}`} onClick={() => tapLight()}>
            <ion-icon name={tabs[2]!.icon} />
            {tabs[2]!.label}
          </NavLink>
          <button
            className={`tab-item${manageItems.some((m) => m.to === location.pathname) || menuOpen ? ' tab-item-active' : ''}`}
            onClick={() => { tapLight(); setMenuOpen(true); }}
          >
            <ion-icon name="menu" />
            More
          </button>
        </div>
      </nav>

      {addOpen && (
        <Modal title="Quick add" onClose={() => setAddOpen(false)}>
          <div className="sheet-stack">
            {quickAdd.map((q) => (
              <button key={q.label} className="sheet-row" onClick={() => go(q.to, { create: true })}>
                <span className="sheet-row-icon"><ion-icon name={q.icon} /></span>
                <span className="sheet-row-text">
                  <span className="sheet-row-title">{q.label}</span>
                  <span className="sheet-row-sub">{q.sub}</span>
                </span>
                <ion-icon name="chevron-forward" className="sheet-row-chevron" />
              </button>
            ))}
          </div>
        </Modal>
      )}

      {switchOpen && (
        <Modal title="Who's using Chortle?" onClose={() => setSwitchOpen(false)}>
          <div className="sheet-stack">
            <button
              className={`sheet-row${!isActingAsShared ? ' sheet-row--selected' : ''}`}
              onClick={() => { tapLight(); switchTo(null); setSwitchOpen(false); }}
            >
              {primaryMember ? (
                <MemberAvatar member={primaryMember} size="2.25rem" fontSize="0.9rem" />
              ) : (
                <div className="avatar" style={{ width: '2.25rem', height: '2.25rem' }}>{avatarLetter}</div>
              )}
              <span className="sheet-row-text">
                <span className="sheet-row-title">{primaryMember?.memberName || email}</span>
                <span className="sheet-row-sub">You</span>
              </span>
              {!isActingAsShared && <ion-icon name="checkmark-circle" className="sheet-row-check" />}
            </button>
            {switchableSharedMembers.map((m) => {
              // With one household the household name is noise; with several it
              // disambiguates same-named users.
              const householdName =
                (households?.length ?? 0) > 1
                  ? households?.find((h) => h.householdId === m.householdId)?.name
                  : undefined;
              return (
                <button
                  key={m.userId}
                  className={`sheet-row${activeMember?.userId === m.userId ? ' sheet-row--selected' : ''}`}
                  onClick={() => { tapLight(); switchTo(m); setSwitchOpen(false); }}
                >
                  <MemberAvatar member={m} size="2.25rem" fontSize="0.9rem" />
                  <span className="sheet-row-text">
                    <span className="sheet-row-title">{m.memberName}</span>
                    <span className="sheet-row-sub">
                      {householdName ? `Shared device user · ${householdName}` : 'Shared device user'}
                    </span>
                  </span>
                  {activeMember?.userId === m.userId && <ion-icon name="checkmark-circle" className="sheet-row-check" />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}

      {menuOpen && (
        <Modal title="Menu" onClose={() => setMenuOpen(false)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div className="avatar" style={{ width: 46, height: 46, fontSize: '1.05rem' }}>{avatarLetter}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{currentHousehold?.name ?? 'Chortle'}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</div>
            </div>
          </div>

          <p className="section-label">Notifications</p>
          <div className="sheet-row" style={{ cursor: 'default', marginBottom: '1.25rem' }}>
            <span className="sheet-row-icon"><ion-icon name="notifications" /></span>
            <span className="sheet-row-text">
              <span className="sheet-row-title">Push notifications</span>
              <span className="sheet-row-sub">
                {isActingAsShared
                  ? 'Switch back to yourself to change this'
                  : pushSupported() ? 'Briefings & housemate cheers' : 'Available in the Chortle app'}
              </span>
            </span>
            {pushSupported() ? (
              <Switch
                checked={pushOn}
                onChange={(next) => togglePush.mutate(next)}
                disabled={togglePush.isPending || isActingAsShared}
                label="Push notifications"
              />
            ) : (
              <span className="badge badge-neutral">In-app</span>
            )}
          </div>

          <p className="section-label">Time off</p>
          <button
            className="sheet-row"
            style={{ marginBottom: '1.25rem' }}
            disabled={isActingAsShared}
            onClick={() => { tapLight(); setMenuOpen(false); setBreakOpen(true); }}
          >
            <span className="sheet-row-icon"><ion-icon name={currentBreak ? 'sunny' : 'airplane'} /></span>
            <span className="sheet-row-text">
              <span className="sheet-row-title">{currentBreak ? 'End my break' : 'Take a break'}</span>
              <span className="sheet-row-sub">
                {isActingAsShared
                  ? 'Switch back to yourself to change this'
                  : currentBreak
                    ? `On a break until ${breakUntilLabel}`
                    : 'Pause chores while you’re away'}
              </span>
            </span>
            <ion-icon name="chevron-forward" className="sheet-row-chevron" />
          </button>

          <p className="section-label">Manage</p>
          <div className="sheet-stack" style={{ marginBottom: '1.25rem' }}>
            {manageItems.map((m) => (
              <button key={m.to} className="sheet-row" onClick={() => go(m.to)}>
                <span className="sheet-row-icon"><ion-icon name={m.icon} /></span>
                <span className="sheet-row-text">
                  <span className="sheet-row-title">{m.label}</span>
                  <span className="sheet-row-sub">{m.desc}</span>
                </span>
                <ion-icon name="chevron-forward" className="sheet-row-chevron" />
              </button>
            ))}
          </div>

          <button className="btn btn-ghost btn-full" onClick={() => void handleSignOut()}>
            <ion-icon name="log-out-outline" style={{ fontSize: '1.1rem' }} />
            Sign out
          </button>
        </Modal>
      )}

      {breakOpen && <BreakSheet currentBreak={currentBreak} onClose={() => setBreakOpen(false)} />}
    </div>
  );
}
